import AsyncStorage from '@react-native-async-storage/async-storage';
import { createClient } from '@supabase/supabase-js';
import Constants from 'expo-constants';
import { Platform } from 'react-native';
import 'react-native-url-polyfill/auto';
import {
  NetworkError,
  PermissionDeniedError,
  ResourceNotFoundError,
  UnknownApplicationError,
  ValidationError
} from './errors';
import { logger } from './logger';

// In development, read directly from process.env to avoid Expo caching issues
// In production builds, use Constants.expoConfig (compiled at build time)
const supabaseUrl = Platform.OS === 'web' && __DEV__
  ? process.env.EXPO_PUBLIC_SUPABASE_URL
  : Constants.expoConfig?.extra?.supabaseUrl;

const supabaseAnonKey = Platform.OS === 'web' && __DEV__
  ? process.env.EXPO_PUBLIC_SUPABASE_KEY
  : Constants.expoConfig?.extra?.supabaseKey;

/**
 * Resolved Supabase URL / anon key. Exported so helpers that need raw HTTP
 * access to Edge Functions (to read non-2xx status codes that
 * supabase.functions.invoke hides) can reuse the same resolved values.
 */
export const SUPABASE_URL: string = supabaseUrl;
export const SUPABASE_ANON_KEY: string = supabaseAnonKey;

// Create a cross-platform storage adapter that handles both SSR and client
const storageAdapter = {
  getItem: async (key: string) => {
    if (Platform.OS === 'web') {
      // Check if we're in browser environment
      if (typeof window !== 'undefined' && window.localStorage) {
        try {
          return window.localStorage.getItem(key);
        } catch (error) {
          logger.error('localStorage.getItem error:', error);
          return null;
        }
      }
      // SSR or no localStorage available
      return null;
    }
    // Mobile - use AsyncStorage
    return AsyncStorage.getItem(key);
  },
  setItem: async (key: string, value: string) => {
    if (Platform.OS === 'web') {
      // Check if we're in browser environment
      if (typeof window !== 'undefined' && window.localStorage) {
        try {
          window.localStorage.setItem(key, value);
        } catch (error) {
          logger.error('localStorage.setItem error:', error);
        }
      }
      // SSR or no localStorage - no-op
      return;
    }
    // Mobile - use AsyncStorage
    return AsyncStorage.setItem(key, value);
  },
  removeItem: async (key: string) => {
    if (Platform.OS === 'web') {
      // Check if we're in browser environment
      if (typeof window !== 'undefined' && window.localStorage) {
        try {
          window.localStorage.removeItem(key);
        } catch (error) {
          logger.error('localStorage.removeItem error:', error);
        }
      }
      // SSR or no localStorage - no-op
      return;
    }
    // Mobile - use AsyncStorage
    return AsyncStorage.removeItem(key);
  },
};

/**
 * Retries a request once with a freshly refreshed access token when the API
 * answers 401. supabase-js only refreshes when *it* thinks the token expired;
 * a client clock that runs slow, or a query racing the post-wake refresh,
 * sends a token the server already rejects ("JWT expired", PGRST303). The
 * 401 is returned before any work happens server-side, so the retry is safe
 * for writes too. Auth endpoints and anon-key requests are never retried.
 */
async function fetchWithJwtRetry(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
  const response = await fetch(input, init);
  if (response.status !== 401) return response;

  const url = typeof input === 'string' ? input : input instanceof URL ? input.toString() : input.url;
  if (!supabaseUrl || !url.startsWith(supabaseUrl) || url.includes('/auth/v1/')) return response;

  const headers = new Headers(init?.headers);
  const authorization = headers.get('Authorization') ?? '';
  if (!authorization.startsWith('Bearer ') || authorization === `Bearer ${supabaseAnonKey}`) return response;

  let freshToken: string | undefined;
  try {
    const { data, error } = await supabase.auth.refreshSession();
    if (error) return response;
    freshToken = data.session?.access_token;
  } catch {
    return response;
  }
  if (!freshToken || `Bearer ${freshToken}` === authorization) return response;

  headers.set('Authorization', `Bearer ${freshToken}`);
  return fetch(input, { ...init, headers });
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  global: { fetch: fetchWithJwtRetry },
  auth: {
    storage: storageAdapter,
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: Platform.OS === 'web',
    ...(Platform.OS === 'web' && {
      lock: async <T>(_name: string, _acquireTimeout: number, fn: () => Promise<T>): Promise<T> => fn(),
    }),
  },
});

const emptyStorage = {
  getItem: async () => null,
  setItem: async () => {},
  removeItem: async () => {},
};

export const supabaseAnon = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    storage: emptyStorage,
    autoRefreshToken: false,
    persistSession: false,
    detectSessionInUrl: false,
  },
});

// Define the shape of the data returned by the Supabase client
interface SupabaseSingleResponse<T> {
  data: T | null;
  error: { message: string; code: string; details?: string; hint?: string } | null;
  count?: number;
  status?: number;
  statusText?: string;
}

interface SupabaseListResponse<T> {
  data: T[] | null;
  error: { message: string; code: string; details?: string; hint?: string } | null;
  count?: number;
  status?: number;
  statusText?: string;
}

/**
 * Internal helper to handle Supabase errors consistently
 * Translates Supabase errors into structured CustomError types
 */
function handleSupabaseError(
  error: { message: string; code: string; details?: string; hint?: string },
  status?: number
): never {
  // a. Custom RPC error codes (P0403 = Forbidden, P0404 = Not Found)
  if (error.code === 'P0403' || status === 403) {
    throw new PermissionDeniedError(error.message || 'Access denied due to insufficient permissions.', error.code);
  }

  if (error.code === 'P0404' || status === 404) {
    throw new ResourceNotFoundError(error.message || 'The requested item was not found.');
  }

  // b. RLS / Permission Denied (PostgreSQL error code)
  if (error.code === '42501') {
    throw new PermissionDeniedError(undefined, error.code);
  }

  // c. Network / Timeout Errors (Often status 503/504 or specific messages)
  const isNetworkError =
    error.message.includes('timed out') ||
    error.message.includes('Failed to fetch') || // Chrome/Firefox
    error.message.includes('Load failed') || // Safari
    error.code === '504' || // Gateway timeout
    status === 503; // Service unavailable

  if (isNetworkError) {
    throw new NetworkError('Could not connect to the server. Please check your connection and try again.', error.code);
  }

  // d. Validation Errors (Database constraints, malformed input in RPC)
  if (error.code && ['23502', '23503', '23514'].includes(error.code)) {
    // 235xx codes are common PostgreSQL constraint violations (not null, foreign key)
    throw new ValidationError(`Database constraint failed: ${error.message}`, error.code);
  }

  // e. All other unexpected errors (e.g., server internal errors)
  throw new UnknownApplicationError(`Supabase Error: ${error.message}`, error.code);
}

/**
 * Handle Supabase requests that return a single record
 * Automatically throws ResourceNotFoundError if the record is not found
 * 
 * @param promise - The promise from a Supabase query (typically with .single())
 * @returns The single record of type T
 * @throws ResourceNotFoundError if no record found
 * @throws Other CustomErrors for database/network issues
 * 
 * @example
 * const lesson: Lesson = await handleSupabaseSingle(
 *   supabase.from('lesson').select('*').eq('id', 123).single()
 * );
 */
export async function handleSupabaseSingle<T>(
  promise: Promise<SupabaseSingleResponse<T>>
): Promise<T> {
  const { data, error, status } = await promise;

  // Handle errors
  if (error) {
    handleSupabaseError(error, status);
  }

  // Check for missing resource
  if (data === null) {
    throw new ResourceNotFoundError();
  }

  return data;
}

/**
 * Handle Supabase requests that return multiple records
 * Returns an empty array if no records found (not an error)
 * 
 * @param promise - The promise from a Supabase query
 * @returns Array of records of type T (empty array if none found)
 * @throws CustomErrors for database/network issues
 * 
 * @example
 * const lessons: Lesson[] = await handleSupabaseList(
 *   supabase.from('lesson').select('*').eq('course_id', 25)
 * );
 */
export async function handleSupabaseList<T>(
  promise: Promise<SupabaseListResponse<T>>
): Promise<T[]> {
  const { data, error, status } = await promise;

  // Handle errors
  if (error) {
    handleSupabaseError(error, status);
  }

  // Return empty array if no data (not an error for lists)
  return data || [];
}